import { DisplayAsset } from '@1stLog-engineering/file-uploads'
import lodash, { find, first, isEmpty, map } from 'lodash'

import { TShipmentApiReusableBox } from '../../ShipmentApi/types'
import {
  TOrderEntryColiService,
  TOrderEntryReusableBox,
  TOrderEntryShipmentService,
} from '../AdditionalServices/types'
import { stringifyCustoms } from '../Form/Customs/utils'
import { TOrderEntryFormValues, TOrderEntryPayload } from '../types'

function formatShipmentServices(services): object {
  // console.log("formatShipmentServices", services)

  const result = {}
  lodash.forEach(services, ({ isEnabled, values, name }) => {
    if (!isEnabled) {
      return
    }
    const serviceObject = {}
    lodash.forEach(values, (v) => {
      const { name, value } = v
      lodash.set(serviceObject, name, v[name] || value)
    })
    lodash.set(result, name, serviceObject)
  })
  return result
}

function fixHazmat(items): Array<object> {
  // console.log("fixHazmat", items)
  const result = lodash.flatten(
    lodash.map(items, (item) => {
      const res: any[] = []
      for (let i = 0; i < lodash.get(item, 'count', 1); i++) {
        res.push(item)
      }
      return res
    }),
  )
  return result
}

function formatColiServices(services): object {
  // console.log("formatColiServices", services)
  const result = {}
  const LOCAL_SERVICES = ['RBX']
  lodash.forEach(services, ({ isEnabled, values, name }) => {
    if (!isEnabled || LOCAL_SERVICES.includes(name)) {
      return
    }
    // hazmats
    if (name === 'GGS') {
      const hazmats = fixHazmat(
        lodash.get(lodash.first(lodash.values(values)), 'value'),
      )
      lodash.set(result, name, hazmats)
    }
    // all other services
    else {
      const serviceObject = {}
      lodash.forEach(values, ({ name, value, isMainMirror }) => {
        // if main mirror, then the whole service is a true boolean
        lodash.set(serviceObject, name, isMainMirror ? true : value)
      })
      lodash.set(result, name, serviceObject)
    }
  })
  // console.log("formatted coliServices", result)
  return result
}

function getExtraShipmentServices(values): object {
  const services = [
    lodash.get(values, 'pickup.pickupTime.settings.additionalService'),
    lodash.get(values, 'pickup.deliveryTime.settings.additionalService'),
    lodash.get(values, 'delivery.pickupTime.settings.additionalService'),
    lodash.get(values, 'delivery.deliveryTime.settings.additionalService'),
  ].filter((s) => !lodash.isEmpty(s))
  return lodash.keyBy(services, 'name')
}

function formatColi(coli): any {
  // const { zusatzserviceSelection } = coli
  // console.log("formatColi", coli)
  const { services } = coli
  // const selectedColiServices = lodash
  //   .map(zusatzserviceSelection || {}, (value, key) => ({
  //     value,
  //     key
  //   }))
  //   .filter(s => !!s.value)
  //   .map(s => s.key)

  const newColiPick = lodash.pick(coli, [
    'width',
    'length',
    'height',
    'weight',
    'volume',
    'packaging',
    'content',
    'barcode',
    'barcode1',
    'coliReferenceText',
  ])

  // reusable box
  const reusableBoxService = lodash.find(
    services,
    (s) => s.name === 'RBX' && s.isEnabled,
  )
  let reusableBoxObject = {
    isReusableBoxEnabled: false,
    reusableBox: undefined,
    reusableBoxBarcode: undefined,
  }
  if (reusableBoxService) {
    const value = lodash.get(
      lodash.first(lodash.get(reusableBoxService, 'values')),
      'value',
    )
    reusableBoxObject = {
      reusableBox: lodash.get(value, 'data'),
      reusableBoxBarcode: lodash.get(value, 'identifier'),
      isReusableBoxEnabled: true,
    }
  }
  const newColi = {
    ...newColiPick,
    width: coli.width || 0,
    length: coli.length || 0,
    height: coli.height || 0,
    weight: coli.weight || 0,
    volume: coli.volume || 0,
    volumeWeight: coli.volumeWeight || 0,
    coliServices: formatColiServices(services),
    barcode:
      lodash.isString(newColiPick.barcode) ||
      lodash.isString(newColiPick.barcode1)
        ? (newColiPick.barcode || newColiPick.barcode1 || '').trim()
        : null,
    ...reusableBoxObject,
  }

  return newColi
}

async function formatOrderEntry(
  values: TOrderEntryFormValues,
  options: {
    pickupProducts: any
    deliveryProducts: any
    preset: any
    currentPrinter: any
  },
): Promise<TOrderEntryPayload> {
  const {
    pickupProducts,
    deliveryProducts,
    preset = {},
    currentPrinter,
  } = options || {}

  // console.log("formatOrderEntry", { values, options })

  const {
    isPickup,
    isDelivery,
    pickup,
    delivery,
    invoice,
    customer,
    neutralSender,
    importer,
    colis,
    isPrintRequested,
    shipmentServices,

    customerReferenceText,
    requestLabelPrint,
    // shipmentServiceSelection,
    // shipmentServiceSettings,
    freightTerms,
    valueOfGoods,
    customsTerms,
    proformaInvoice,
    customs,
    clientNotifications,
    clientNotificationsDisabled,
    sla,
    additionalCustomsDocs,
    invoiceDocument,
    clientNotificationsLanguage,
    clientNotificationsAdditionalPhones,
    clientNotificationsAdditionalEmails,
  } = values

  // const { isPickupAndDelivery, allowPickupProduct, allowDeliveryProduct } = preset
  // const allowPickupProduct = isPickup || requirePickupProduct
  // const allowDeliveryProduct = isDelivery || requireDeliveryProduct

  // const isPickupAndDelivery = isPickup && isDelivery

  // isPickupAndDelivery: pickup and delivery products can be selected, requires endpoint update

  // if (isPickupAndDelivery) {
  //   console.warn(
  //     "TODO: pickup and delivery products not supported by the endpoint yet"
  //   )
  //   return null
  // }

  // const payload = parseValues(values)
  // if (values.isPrintRequested) {
  //   payload.selectedPrinter = currentPrinter
  // }
  // global.lastOrderPayload = payload

  // check if pickup or delivery product has to be picked

  const selectedProductId = lodash.get(
    isPickup ? pickup : delivery,
    'selectedProductId',
  )
  const product = lodash.find(
    isPickup ? pickupProducts : deliveryProducts,
    (p) => p.id === selectedProductId,
  )

  // time settings
  // pickup.pickupTime.settings.from/until etc
  const pickupTimeSettings = lodash.get(
    isPickup ? pickup : delivery,
    'pickupTime.settings',
  )
  const deliveryTimeSettings = lodash.get(
    isPickup ? pickup : delivery,
    'deliveryTime.settings',
  )

  let productPayload: any = {
    pickupFromTime: lodash.get(pickupTimeSettings, 'from'),
    pickupUntilTime: lodash.get(pickupTimeSettings, 'until'),
    deliveryFromTime: lodash.get(deliveryTimeSettings, 'from'),
    deliveryUntilTime: lodash.get(deliveryTimeSettings, 'until'),
  }

  //
  if (isPickup && isDelivery) {
    productPayload = {
      pickupFromTime: lodash.get(pickup, 'timeWindow.start'),
      pickupUntilTime: lodash.get(pickup, 'timeWindow.end'),
      deliveryFromTime: lodash.get(delivery, 'timeWindow.start'),
      deliveryUntilTime: lodash.get(delivery, 'timeWindow.end'),
    }
  }

  // Recommended times
  const recommendedTimeFrom = lodash.get(
    deliveryTimeSettings,
    'recommendedTimeFrom',
  )
  const recommendedTimeUntil = lodash.get(
    deliveryTimeSettings,
    'recommendedTimeUntil',
  )
  productPayload.recommendedTimeFrom = recommendedTimeFrom || null
  productPayload.recommendedTimeUntil = recommendedTimeUntil || null

  const requestDetails = lodash.get(
    isPickup ? pickup : delivery,
    'confirmation',
  )

  const pickupProduct = lodash.pick(pickup, [
    'isManualDisposition',
    'timeWindow',
    'productSettings',
  ])

  const deliveryProduct = lodash.pick(delivery, [
    'isManualDisposition',
    'timeWindow',
    'productSettings',
  ])

  const extraShipmentServices = getExtraShipmentServices(values)

  // placeholder
  const selectedPrinter = isPrintRequested
    ? lodash.get(currentPrinter, 'id')
    : undefined

  const result = {
    // actually used
    pickupAddress: lodash.get(pickup, 'address'),
    deliveryAddress: lodash.get(delivery, 'address'),
    neutralSenderAddress: lodash.get(neutralSender, 'address'),
    isNeutralSendingActive: lodash.get(neutralSender, 'isActive'),
    invoiceAddress: invoice?.freightCollect
      ? delivery?.address
      : invoice?.address,
    freightCollect: invoice?.freightCollect || false,
    freightCollectReference: invoice?.freightCollectReference || null,
    customerAddress: lodash.get(customer, 'address'),
    transport: {
      // TODO: make new payload with pickup/delivery time settings
      product: {
        id: lodash.get(product, 'id'),
        ...productPayload,
        pickupRequestDetails: lodash.pick(pickupTimeSettings, [
          'phone',
          'email',
          'infos',
        ]),
        deliveryRequestDetails: lodash.pick(deliveryTimeSettings, [
          'phone',
          'email',
          'infos',
        ]),
        pickupTimeSettings,
        deliveryTimeSettings,
      }, // product is internal frontend format here
      pickup: lodash.get(pickupTimeSettings, 'additionalService.name', 'std'), // only checked if std or not
      delivery: lodash.get(
        deliveryTimeSettings,
        'additionalService.name',
        'std',
      ), // only checked if std or not
      shouldWaitForAvis: undefined,
      requestDetails,
      pickupProduct,
      deliveryProduct: !isEmpty(deliveryProduct)
        ? {
            ...deliveryProduct,
            recommendedTimeFrom: recommendedTimeFrom || null,
            recommendedTimeUntil: recommendedTimeUntil || null,
          }
        : null,
      sla,
    },
    isPrintRequested,
    selectedPrinter,
    colis: lodash.map(colis, formatColi),
    customerReferenceText,
    isSleeping: undefined,
    massCreationReference: undefined,
    shipmentServices: {
      ...formatShipmentServices(shipmentServices),
      ...extraShipmentServices,
    },
    freightTerms,
    valueOfGoods,

    requestLabelPrint: !!requestLabelPrint,
    customsAddress: lodash.get(importer, 'address'),
    customs: stringifyCustoms(customs),
    customsTerms,
    proformaInvoice,
    clientNotifications: clientNotificationsDisabled ? [] : clientNotifications,
    clientNotificationsAdditionalPhones: map(
      clientNotificationsAdditionalPhones,
      ({ phone }) => phone,
    ),
    clientNotificationsAdditionalEmails: map(
      clientNotificationsAdditionalEmails,
      ({ email }) => email,
    ),
    clientNotificationsLanguage,

    // OrderEntry flag
    v2: true,

    // documents
    additionalCustomsDocs: getMedia(additionalCustomsDocs),
    invoiceDocument: getMedia(invoiceDocument),
    codeDelivery: getCodeDelivery(shipmentServices ?? []),
    idCheck: getIdentityCheck(shipmentServices ?? []),
  }
  return result
}

export default formatOrderEntry

export function getReusableBox(
  services: TOrderEntryColiService[] | undefined,
): TShipmentApiReusableBox | null {
  if (!services) {
    return null
  }
  const service = find(services, (s) => s.name === 'RBX')
  if (!service?.isEnabled) {
    return null
  }
  const rbx = first(service?.values)?.value as unknown as TOrderEntryReusableBox

  if (!rbx) {
    return null
  }
  return {
    identifier: rbx?.identifier,
    type: rbx?.data?.type,
  }
}

export function getCodeDelivery(
  services: TOrderEntryShipmentService[],
): any | null {
  const service = find(services, (s) => s.name === 'CZST')
  if (!service?.isEnabled) {
    return null
  }
  const value = service.values[0].value
  return {
    code: value,
    // type: '', // TODO:
  }
}

export function getIdentityCheck(services: TOrderEntryShipmentService[]): any {
  const service = find(services, (s) => s.name === 'IDCK')
  if (!service?.isEnabled) {
    return null
  }
  return {
    required: true,
  }
}

export function getMedia(files: DisplayAsset[]): {
  name: string
  extension?: string
  id: number | string
  type: string
  data: string | undefined
}[] {
  return map(files, (file) => {
    return {
      name: file.name,
      extension: String(file.extension) || '',
      id: file.id,
      type: file.type,
      data: file.data,
    }
  })
}
